import { EmptyState } from '@uag/react-core';
import { memo, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Object3D } from 'three';
import { useShallow } from 'zustand/shallow';

import { Heatmap } from 'app/modules/wallforming/components/content-container/heatmap/Heatmap';
import { HeatmapRenderer } from 'app/modules/wallforming/components/content-container/heatmap/HeatmapRenderer';
import { HubConnectionContainer } from 'app/modules/wallforming/components/content-container/HubConnectionContainer';
import { MainCanvas } from 'app/modules/wallforming/components/content-container/MainCanvas';
import { SceneStatusBar } from 'app/modules/wallforming/components/content-container/scene-status-bar/SceneStatusBar';
import { InputBindingPanel } from 'app/modules/wallforming/components/input-binding/InputBindingPanel';
import { MenuBar } from 'app/modules/wallforming/components/menu-container/MenuBar';
import { HistoryPanel } from 'app/modules/wallforming/components/placement-history/HistoryPanel';
import { ProjectsManager } from 'app/modules/wallforming/components/projects-manager/ProjectsManager';
import { CameraOrientationCube } from 'app/modules/wallforming/components/scenes/camera-controls/CameraOrientationCube';
import { MainCameraControls } from 'app/modules/wallforming/components/scenes/camera-controls/MainCameraControls';
import { FormingScene } from 'app/modules/wallforming/components/scenes/FormingScene';
import { MainScene } from 'app/modules/wallforming/components/scenes/MainScene';
import { ArticleContextMenu } from 'app/modules/wallforming/components/scenes/objects/articles/ArticleContextMenu';
import { Articles } from 'app/modules/wallforming/components/scenes/objects/articles/Articles';
import { Building } from 'app/modules/wallforming/components/scenes/objects/building/Building';
import { PlanProjection } from 'app/modules/wallforming/components/scenes/objects/dimensions/PlanProjection';
import { ConnectorSnapPointsMarker } from 'app/modules/wallforming/components/scenes/objects/helpers/ConnectorSnapPointsMarker';
import { DraggingBox } from 'app/modules/wallforming/components/scenes/objects/helpers/DraggingBox';
import { ReferenceMarker } from 'app/modules/wallforming/components/scenes/objects/helpers/ReferenceMarker';
import { SnapEdgeMarker } from 'app/modules/wallforming/components/scenes/objects/helpers/SnapEdgeMarker';
import { SnapPointMarker } from 'app/modules/wallforming/components/scenes/objects/helpers/SnapPointMarker';
import { PoiNavigator } from 'app/modules/wallforming/components/scenes/objects/pois/PoiNavigator';
import { PoiTypeStepper } from 'app/modules/wallforming/components/scenes/objects/pois/PoiTypeStepper';
import { SceneEnvironment } from 'app/modules/wallforming/components/scenes/SceneEnvironment';
import { SceneLighting } from 'app/modules/wallforming/components/scenes/SceneLighting';
import { ScenePostProcessing } from 'app/modules/wallforming/components/scenes/ScenePostProcessing';
import { Toolbar } from 'app/modules/wallforming/components/scenes/toolbar/ToolBar';
import { CreateGroupModal } from 'app/modules/wallforming/components/setting-container/article-group/CreateGroupModal';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { MAIN_DIV_ID } from 'shared/constants';
import { useIsStandaloneMode } from 'shared/hooks/useIsStandaloneMode';

import 'app/modules/wallforming/custom-styles/customEmptyStateStyle.css';

export const ContentContainer = memo(() => {
    Object3D.DEFAULT_UP.set(0, 0, 1);

    const cameraFrustumViewerRef = usePlacementStore(useShallow((state) => state.cameraFrustumViewerRef));
    const isStandaloneMode = useIsStandaloneMode();
    const heatmapRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        // Close camera frustum viewer on searchparams update or on window closing
        const handleBeforeUnload = () => {
            cameraFrustumViewerRef.current?.close();
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [cameraFrustumViewerRef]);

    return (
        <div className="flex flex-col w-full h-full overflow-hidden">
            <MenuBar />
            <CreateGroupModal />
            <HistoryPanel />
            {isStandaloneMode && <ProjectsManager />}
            <InputBindingPanel />

            <div
                className="flex flex-col w-full h-full flex-1 overflow-hidden border-border-light relative focus:outline-none"
                id={MAIN_DIV_ID}
                tabIndex={0}
                onPointerEnter={(e) => e.currentTarget.focus()}
            >
                <Toolbar />
                <ArticleContextMenu />
                <PoiTypeStepper />

                <ErrorBoundary
                    key={location.pathname}
                    fallbackRender={({ error }) => (
                        <EmptyState
                            action={() => window.location.reload()}
                            actionLabel="Refresh"
                            className="h-full w-full"
                            type="error"
                        >
                            {error instanceof Error ? error.message : String(error)}
                        </EmptyState>
                    )}
                >
                    <HubConnectionContainer>
                        <MainCanvas>
                            <MainCameraControls />
                            <CameraOrientationCube />

                            <MainScene>
                                <Building />
                                <PoiNavigator />

                                <FormingScene>
                                    <ConnectorSnapPointsMarker />
                                    <SnapPointMarker />
                                    <SnapEdgeMarker />
                                    <ReferenceMarker />
                                    <PlanProjection />
                                    <Articles isMainCanvas={true} />
                                    <DraggingBox />
                                </FormingScene>
                            </MainScene>

                            <SceneEnvironment />
                            <SceneLighting />
                            <ScenePostProcessing />
                            <HeatmapRenderer canvasRef={heatmapRef} />
                        </MainCanvas>

                        <Heatmap heatmapRef={heatmapRef} />
                        <SceneStatusBar />
                    </HubConnectionContainer>
                </ErrorBoundary>
            </div>
        </div>
    );
});

ContentContainer.displayName = 'ContentContainer';
